import { isTrackingEnabled } from "@/lib/trackingEnvironment";
import { installDataLayerDenylist } from "@/lib/gtmDataLayer";

const GTM_CONTAINER_ID = "GTM-T8PVRK33";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    __zooiConsentDefaultsPushed?: boolean;
    __zooiGtmInitialized?: boolean;
  }
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

// gtag.js spec requires pushing the real `arguments` object (Arguments-like, not Array)
// so GTM/GA4 reads positional args correctly. Must use `function`, not arrow.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function pushGtagCommand(this: unknown, ..._args: unknown[]) {
  const dataLayer = ensureDataLayer() as unknown[];
  // eslint-disable-next-line prefer-rest-params
  dataLayer.push(arguments);
}

export function pushDefaultConsent() {
  if (typeof window === "undefined" || !isTrackingEnabled() || window.__zooiConsentDefaultsPushed) {
    return;
  }

  window.__zooiConsentDefaultsPushed = true;

  pushGtagCommand("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  pushGtagCommand("set", "ads_data_redaction", true);
  pushGtagCommand("set", "url_passthrough", true);
}

export function grantTrackingConsent() {
  if (typeof window === "undefined" || !isTrackingEnabled()) {
    return;
  }

  pushGtagCommand("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
}

function loadGtmScript() {
  if (document.querySelector(`script[data-gtm-container="${GTM_CONTAINER_ID}"]`)) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  script.dataset.gtmContainer = GTM_CONTAINER_ID;
  // Low priority so it doesn't compete with user interactions for bandwidth/CPU
  (script as HTMLScriptElement & { fetchPriority: string }).fetchPriority = "low";

  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  document.head.appendChild(script);
}

export function bootstrapGoogleTracking() {
  if (typeof window === "undefined" || !isTrackingEnabled()) {
    return;
  }

  // Install purchase denylist proxy BEFORE consent/GTM so it catches everything
  installDataLayerDenylist();

  pushDefaultConsent();

  try {
    if (window.localStorage.getItem("cookieConsent") === "accepted") {
      grantTrackingConsent();
    }
  } catch {
    // Ignore storage access issues
  }

  if (window.__zooiGtmInitialized) {
    return;
  }

  window.__zooiGtmInitialized = true;
  ensureDataLayer().push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });
  loadGtmScript();
}
